import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { IdentityService } from 'app/services/authentication/identity.service';
import { SessionService } from 'app/services/session/session.service';
import { StorageKeyEnum } from 'app/services/storage/storageKeyEnum.enum';
import { ProgressBarService } from 'app/services/components/progress-bar/progress-bar.service';

// Saltar barra en alguna request:
// this.http.get(url, { headers: new HttpHeaders({ 'X-Skip-Progress': '1' }) });
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private activeRequests = 0;

    constructor(
        private router: Router,
        private _identityService: IdentityService,
        private _sessionService: SessionService,
        private _progress: ProgressBarService
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // 1) Rutas (o patrones) a excluir del interceptor
        //    (podés usar startsWith/includes/regex según tu API)
        const excludedRoutes = ['auth/login', 'assets/i18n/', 'assets/l10n/'];

        // 2) Flag opcional para saltar progress bar en una request concreta
        const skipProgress = req.headers.has('X-Skip-Progress');

        // Si la URL está en la lista de excluidas, paso directo (pero igual respeto el progress skip)
        if (excludedRoutes.some(route => req.url.includes(route))) {
            return next.handle(req);
        }

        // 3) Chequear login antes de seguir
        return this.isLogged().pipe(
            switchMap(isLogged => {
                if (!isLogged) {
                    this.handleNotLoggedIn();
                    return throwError(() => new Error('User is not logged in'));
                }

                // 4) Manejo de progress bar (concurrencia)
                if (!skipProgress) {
                    this.bumpUp();
                }

                return next.handle(req).pipe(
                    tap(event => {
                        if (event instanceof HttpResponse && event.status === 200) {
                            // Respuesta OK: podés agregar lógica si querés.
                        }
                    }),
                    catchError((error: HttpErrorResponse) => {
                        if (error.status === 401) {
                            this.handle401Error();
                        }
                        return throwError(() => error);
                    }),
                    finalize(() => {
                        if (!skipProgress) {
                            this.bumpDown();
                        }
                    })
                );
            })
        );
    }

    // --- Helpers de sesión / progreso ---

    private isLogged(): Observable<boolean> {
        return of(this._sessionService.getItem(StorageKeyEnum.sessionToken) !== null);
    }

    private handleNotLoggedIn() {
        this._identityService.logOut();
        this.router.navigate(['/auth/login']);
    }

    private handle401Error(): void {
        this._identityService.logOut();
        this.router.navigate(['/auth/login']);
    }

    /** Sube el contador y arranca progress al pasar de 0→1 */
    private bumpUp(): void {
        if (this.activeRequests === 0) {
            this._progress.startProgress();
        }
        this.activeRequests++;
    }

    /** Baja el contador y completa progress al llegar a 0 */
    private bumpDown(): void {
        this.activeRequests = Math.max(0, this.activeRequests - 1);
        if (this.activeRequests === 0) {
            this._progress.completeProgress();
        }
    }
}
